import { type KeyboardEvent, type MouseEvent } from "react";

import { useEditorState } from "@tiptap/react";
import {
  CheckIcon,
  LinkIcon,
  PencilIcon,
  UnlinkIcon,
  XIcon,
} from "lucide-react";

import { Button, ButtonGroup } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";

import { useEditorContext } from "../../editor/EditorContext";
import { useFloatingToolbar } from "./context";
import { shouldShowLinkSection } from "./predicates";

/**
 * Link controls for the unified floating toolbar:
 * - selection → link button
 * - edit → URL input + accept / cancel
 * - existing link → URL label + edit / unlink
 */
export const LinkSection = () => {
  const { editor, commands } = useEditorContext();
  const { features, mode, idle, startEditLink, setLinkDraft } =
    useFloatingToolbar();

  const linkState = useEditorState({
    editor,
    selector: ({ editor: instance }) => {
      if (!instance || instance.isDestroyed) {
        return { href: "", empty: true, canShow: false };
      }
      const { empty } = instance.state.selection;
      const href = (instance.getAttributes("link").href as string) || "";
      // Ignore focus here: while editing the URL, focus moves to the input
      // and clearing draft would abort edit mode.
      return {
        href,
        empty,
        canShow: shouldShowLinkSection(instance, instance.state, {
          requireFocus: false,
        }),
      };
    },
  });

  const href = linkState?.href ?? "";
  const empty = linkState?.empty ?? true;
  const canShow = linkState?.canShow ?? false;

  const isEditing = mode.type === "editLink" && canShow;
  const draft = mode.type === "editLink" ? mode.draft : "";

  // Focus once when the input mounts (edit mode opened).
  const focusInput = (node: HTMLInputElement | null) => {
    if (!node) {
      return;
    }
    node.focus({ preventScroll: true });
    node.select();
  };

  if (!features.link || !editor || !canShow) {
    return null;
  }

  // Hide compact chrome while another expanded panel is active.
  if (mode.type === "editFile") {
    return null;
  }

  const startEditing = () => {
    if (empty && href) {
      editor.chain().focus().extendMarkRange("link").run();
    }
    startEditLink(href);
  };

  const cancelEditing = () => {
    idle();
    if (!editor.isDestroyed) {
      editor.commands.focus();
    }
  };

  const submitHref = () => {
    const trimmed = draft.trim();
    idle();
    if (!trimmed) {
      commands.removeLink?.();
      return;
    }
    commands.updateLink?.({ href: trimmed });
  };

  const removeLink = () => {
    commands.removeLink?.();
    idle();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitHref();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <Input
          ref={focusInput}
          type="text"
          value={draft}
          placeholder="Enter link..."
          onChange={(e) => setLinkDraft(e.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          size="small"
          style={{ width: 450 }}
        />
        <ButtonGroup variant="outline" size="small">
          <Button
            type="button"
            aria-label="Apply link"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={submitHref}
          >
            <Icon icon={CheckIcon} />
          </Button>
          <Button
            type="button"
            aria-label="Cancel"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={cancelEditing}
          >
            <Icon icon={XIcon} />
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  if (href) {
    return (
      <div style={{ position: "relative" }}>
        <div
          style={{
            display: "flex",
            position: "absolute",
            padding: "2px 6px",
            bottom: 31,
            left: 1,
            background: "#fff",
            borderRadius: "0.3rem",
            boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.2)",
            alignItems: "center",
            fontSize: "0.75rem",
            lineHeight: "0.85rem",
            wordBreak: "break-all",
            maxWidth: 350,
            width: "max-content",
            gap: 4,
          }}
        >
          <Icon
            icon={LinkIcon}
            size="small"
            style={{ top: 0, position: "relative", width: 10 }}
          />
          {href}
        </div>
        <ButtonGroup size="small">
          <Button
            type="button"
            aria-label="Edit link"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={startEditing}
            variant="outline"
          >
            <Icon icon={PencilIcon} />
          </Button>
          <Button
            type="button"
            aria-label="Remove link"
            onMouseDown={(e: MouseEvent) => e.preventDefault()}
            onClick={removeLink}
            variant="outline"
          >
            <Icon icon={UnlinkIcon} />
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <ButtonGroup size="small">
      <Button
        type="button"
        aria-label="Add link"
        onMouseDown={(e: MouseEvent) => e.preventDefault()}
        onClick={startEditing}
        variant="outline"
      >
        <Icon icon={LinkIcon} />
      </Button>
    </ButtonGroup>
  );
};
